/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { isImageFile, isPdfFile } from '@/utility/fileClassification';
import {
  PREVIEW_COLUMN_LIMIT,
  PREVIEW_ROW_LIMIT,
  SPREADSHEET_LIMITS,
  formatSpreadsheetCell,
  readSpreadsheet,
  type SpreadsheetSheet,
} from '@/utility/readSpreadsheet';

/**
 * Call once at app startup. Pass the worker URL constructed with new URL() from
 * the consuming app so pdfjs-dist resolves against the app's own node_modules.
 *
 * @example
 * setupFilePreview(
 *   new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()
 * );
 */
export function setupFilePreview(workerSrc: string) {
  pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
}

/* ---------------------------------- */
/* UTILS */
/* ---------------------------------- */

const EXCEL_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
]);

export function isExcelFile(url?: string, mimeType?: string) {
  return (
    EXCEL_MIME_TYPES.has(mimeType ?? '') || /\.xlsx?(\?|$)/i.test(url ?? '')
  );
}

/**
 * A legacy binary `.xls` (Excel 97-2003). The preview reads `.xlsx` only: no
 * parser on the npm registry opens the binary format, and the one we used to
 * ship (SheetJS from the registry, frozen at 0.18.5) carries unfixed advisories.
 * A file named `.xlsx` wins over the `application/vnd.ms-excel` type some
 * browsers attach to it.
 */
export function isLegacyExcelFile(url?: string, mimeType?: string) {
  if (/\.xls(\?|$)/i.test(url ?? '')) return true;
  if (/\.xlsx(\?|$)/i.test(url ?? '')) return false;
  return mimeType === 'application/vnd.ms-excel';
}

/**
 * Whether `FilePreview` can show this file in the page: images, PDFs and
 * `.xlsx` spreadsheets. Anything else (Word, zip, a legacy `.xls`, ...) needs a
 * download to open, and is never handed to a third-party viewer: these are
 * confidential HR files.
 */
export function canPreviewFile(url?: string, mimeType?: string | null) {
  if (!url) return false;
  const type = mimeType ?? undefined;
  return (
    isImageFile(url, type) ||
    isPdfFile(url, type) ||
    (isExcelFile(url, type) && !isLegacyExcelFile(url, type))
  );
}

/* ---------------------------------- */
/* SHARED FALLBACK */
/* ---------------------------------- */

function PreviewFallback({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center h-full text-sm text-gray-400">
      {message}
    </div>
  );
}

/* ---------------------------------- */
/* EXCEL PREVIEW */
/* ---------------------------------- */

function ExcelPreview({
  url,
  loadFailed,
}: {
  url: string;
  loadFailed: string;
}) {
  const { t: tLibs } = useTranslation(TranslationNS.LibsCommon);
  const [sheets, setSheets] = useState<SpreadsheetSheet[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSheets([]);
    setActiveIndex(0);
    fetch(url, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        // Refuse a file the parser would refuse anyway before it comes down.
        const declared = Number(res.headers?.get?.('content-length'));
        if (declared > SPREADSHEET_LIMITS.fileBytes) {
          throw new Error('Spreadsheet too large to preview');
        }
        return res.arrayBuffer();
      })
      .then(readSpreadsheet)
      .then((parsed) => {
        if (controller.signal.aborted) return;
        if (parsed.length === 0) throw new Error('Spreadsheet has no sheets');
        setSheets(parsed);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError' && !controller.signal.aborted) {
          setError(loadFailed);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [url, loadFailed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={24} className="animate-spin text-gray-300" />
      </div>
    );
  }

  if (error) return <PreviewFallback message={error} />;

  const rows = sheets[activeIndex]?.rows ?? [];
  const shownRows = rows.slice(0, PREVIEW_ROW_LIMIT);
  const truncated =
    rows.length > PREVIEW_ROW_LIMIT ||
    rows.some((row) => row.length > PREVIEW_COLUMN_LIMIT);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {sheets.length > 1 && (
        <div className="flex gap-1 px-3 pt-2 border-b border-gray-200 overflow-x-auto shrink-0">
          {sheets.map((sheet, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`px-3 py-1.5 text-xs rounded-t whitespace-nowrap transition-colors ${
                index === activeIndex
                  ? 'bg-white border border-b-white border-gray-200 font-medium text-gray-800 -mb-px'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
      {truncated && (
        <p className="px-4 pt-3 text-xs text-gray-500 shrink-0">
          {tLibs('file_preview.truncated', {
            rows: PREVIEW_ROW_LIMIT,
            columns: PREVIEW_COLUMN_LIMIT,
          })}
        </p>
      )}
      <div className="overflow-auto flex-1 p-4">
        <table className="text-xs border-collapse">
          <tbody>
            {shownRows.map((row, r) => (
              <tr key={`${activeIndex}-${r}`} className="even:bg-gray-50">
                {row.slice(0, PREVIEW_COLUMN_LIMIT).map((cell, c) => (
                  <td
                    key={`${r}-${c}`}
                    className="border border-gray-200 px-2 py-1 whitespace-nowrap"
                  >
                    {formatSpreadsheetCell(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------------------------- */
/* PDF PREVIEW */
/* ---------------------------------- */

const PDF_PAGE_PLACEHOLDER_HEIGHT = 842;

function PdfPreview({ url, loadFailed }: { url: string; loadFailed: string }) {
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [renderedPages, setRenderedPages] = useState<Set<number>>(new Set());

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!numPages || !containerRef.current) return;
    const root = containerRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const toAdd: number[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            toAdd.push(Number((entry.target as HTMLElement).dataset.page));
          }
        });
        if (toAdd.length) {
          setRenderedPages((prev) => new Set([...prev, ...toAdd]));
        }
      },
      { root, rootMargin: '300px' }
    );
    root.querySelectorAll('[data-page]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [numPages]);

  return (
    <div
      ref={containerRef}
      className="overflow-auto h-full flex flex-col items-center py-4 gap-3"
    >
      <Document
        file={url}
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages);
          setRenderedPages(new Set());
        }}
        loading={
          <div className="flex items-center justify-center h-full pt-16">
            <Loader2 size={24} className="animate-spin text-gray-300" />
          </div>
        }
        error={<PreviewFallback message={loadFailed} />}
      >
        {Array.from({ length: numPages }, (_, i) => {
          const pageNum = i + 1;
          const isRendered = renderedPages.has(pageNum);
          return (
            <div
              key={pageNum}
              data-page={pageNum}
              className="flex flex-col items-center gap-1"
              style={{
                minHeight: isRendered ? undefined : PDF_PAGE_PLACEHOLDER_HEIGHT,
              }}
            >
              {isRendered && <Page pageNumber={pageNum} width={width} />}
              <span className="text-xs py-2 px-4 bg-gray-100">
                {pageNum} / {numPages}
              </span>
            </div>
          );
        })}
      </Document>
    </div>
  );
}

/* ---------------------------------- */
/* IMAGE PREVIEW */
/* ---------------------------------- */

function ImagePreview({
  url,
  loadFailed,
}: {
  url: string;
  loadFailed: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return <PreviewFallback message={loadFailed} />;
  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-50">
      <img
        src={url}
        alt="preview"
        className="max-h-full max-w-full object-contain"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

/* ---------------------------------- */
/* FILE PREVIEW */
/* ---------------------------------- */

interface FilePreviewProps {
  url?: string;
  mimeType?: string;
  /**
   * The portal's `files` translator. Optional: without it the messages come
   * from `libsCommon`, so a shared component can preview a file too.
   */
  t?: TFunction<TranslationNS.Files>;
}

export function FilePreview({ url, mimeType, t }: FilePreviewProps) {
  const { t: tLibs } = useTranslation(TranslationNS.LibsCommon);
  const loadFailed = t
    ? t('preview.load_failed')
    : tLibs('file_preview.load_failed');
  const notAvailable = t
    ? t('preview.not_available')
    : tLibs('file_preview.not_available');

  if (!url) return <PreviewFallback message={notAvailable} />;

  if (isImageFile(url, mimeType))
    return <ImagePreview url={url} loadFailed={loadFailed} />;

  if (isExcelFile(url, mimeType)) {
    // A legacy .xls has no in-page reader: say so, the download is beside it.
    if (isLegacyExcelFile(url, mimeType))
      return <PreviewFallback message={notAvailable} />;
    return <ExcelPreview url={url} loadFailed={loadFailed} />;
  }

  if (isPdfFile(url, mimeType))
    return <PdfPreview url={url} loadFailed={loadFailed} />;

  return <PreviewFallback message={notAvailable} />;
}
